import Container from '../../../atoms/Container';
import ExamInfoFooter from '../../../molecules/Exam/InfoFooter';
import { Typography } from '../../../molecules/Typography';

export type ExamForeignLanguage = 'ingles' | 'espanhol';

interface IExamInfoProps {
  name?: string;
  totalQuestions?: number;
  /** 1 ou 2 no formato de dois dias; ausente em simulado de etapa única. */
  day?: 1 | 2 | null;
  durationLabel?: string | null;
  /** Só o 1º dia tem questões de língua estrangeira. */
  showForeignLanguageChoice?: boolean;
  foreignLanguage?: ExamForeignLanguage;
  isStarting?: boolean;
  onForeignLanguageChange?: (language: ExamForeignLanguage) => void;
  onStartExam: () => void;
}

const FOREIGN_LANGUAGE_OPTIONS: {
  value: ExamForeignLanguage;
  label: string;
}[] = [
  { value: 'ingles', label: 'Inglês' },
  { value: 'espanhol', label: 'Espanhol' },
];

const ExamInfo = ({
  name = 'Simuladão',
  totalQuestions = 90,
  day = null,
  durationLabel = null,
  showForeignLanguageChoice = false,
  foreignLanguage = 'ingles',
  isStarting = false,
  onForeignLanguageChange,
  onStartExam,
}: IExamInfoProps) => {
  return (
    <>
      <Container className='min-h-[800px]'>
        <div className='flex flex-col gap-xxl rounded-lg bg-color-lightest px-12 py-8'>
          <Typography type='title' size='h2'>
            Instruções para o {name}
            {day ? ` — ${day}º dia` : ''}
          </Typography>

          {day && (
            <div className='flex flex-col gap-xl md:flex-row'>
              <div className='w-[220px] flex-shrink-0'>
                <Typography weight='bold'>Sobre este dia:</Typography>
              </div>
              <div className='flex flex-col gap-md'>
                <Typography>
                  Você está iniciando o <strong>{day}º dia</strong> de prova, com{' '}
                  <strong>
                    {day === 1
                      ? 'Linguagens, Códigos e Ciências Humanas'
                      : 'Ciências da Natureza e Matemática'}
                  </strong>
                  .
                </Typography>
                <Typography>
                  Os dois dias são independentes: você pode fazer o outro quando
                  quiser, na ordem que preferir.
                </Typography>
                {durationLabel && (
                  <Typography>
                    O tempo desta prova é de <strong>{durationLabel}</strong>, e
                    começa a contar assim que você iniciar.
                  </Typography>
                )}
              </div>
            </div>
          )}

          {showForeignLanguageChoice && (
            <>
              <hr className='text-mono-color-lightest' />
              <div className='flex flex-col gap-xl md:flex-row'>
                <div className='w-[220px] flex-shrink-0'>
                  <Typography weight='bold'>Língua estrangeira:</Typography>
                </div>
                <div className='flex flex-col gap-md'>
                  <Typography>
                    Como na prova real, escolha o idioma das 5 questões de língua
                    estrangeira. Não é possível trocar depois de iniciar.
                  </Typography>
                  <div
                    className='flex flex-row gap-lg'
                    role='radiogroup'
                    aria-label='Língua estrangeira'
                  >
                    {FOREIGN_LANGUAGE_OPTIONS.map((option) => (
                      <label
                        key={option.value}
                        className='flex cursor-pointer items-center gap-sm'
                      >
                        <input
                          type='radio'
                          name='foreignLanguage'
                          value={option.value}
                          checked={foreignLanguage === option.value}
                          onChange={() => onForeignLanguageChange?.(option.value)}
                          className='h-4 w-4 accent-brand-color-primary-pure'
                        />
                        <Typography>{option.label}</Typography>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          <div className='text-mono-900 flex flex-col gap-xl md:flex-row'>
            <div className='w-[220px] flex-shrink-0'>
              <Typography weight='bold'>Sobre o tempo de prova:</Typography>
            </div>
            <div className='flex flex-col gap-md'>
              <Typography>
                Após o início da prova, <strong>não é possível parar</strong> o
                Simuladão!
              </Typography>
              <Typography>
                O tempo continuará correndo até que você finalize a prova ou o
                tempo se esgote. Caso o tempo se esgote, salvaremos as suas
                respostas e você será redirecionado para a tela de gabarito.
              </Typography>
            </div>
          </div>
          <hr className='text-mono-color-lightest' />
          <div className='flex flex-col gap-xl md:flex-row'>
            <div className='w-[220px] flex-shrink-0'>
              <Typography weight='bold'>Sobre as questões:</Typography>
            </div>
            <div className='flex flex-col gap-md'>
              <Typography>
                Este desafio possui <strong>{totalQuestions} questões</strong>{' '}
                totalmente inéditas no estilo Enem.
              </Typography>
              <Typography>
                As questões são <strong>objetivas</strong>: você pontuará apenas
                se marcar a resposta correta.
              </Typography>
              <Typography>
                Existe apenas <strong>uma resposta correta por questão</strong>.
              </Typography>
              <Typography>
                Respostas em <strong>branco</strong> serão consideradas como
                erradas
              </Typography>
            </div>
          </div>
          <hr className='text-mono-color-lightest' />
          <div className='flex flex-col gap-xl md:flex-row'>
            <div className='w-[220px] flex-shrink-0'>
              <Typography weight='bold'>Sobre o gabarito e ranking:</Typography>
            </div>
            <div className='flex flex-col gap-md'>
              <Typography>
                <strong>
                  Sua participação não contará mais para o Ranking e você não
                  terá acesso a pontuação TRI.
                </strong>{' '}
                No entanto, ao término da resolução, disponibilizaremos o{' '}
                <strong>gabarito</strong> das questões com suas respectivas{' '}
                <strong>resoluções.</strong>
              </Typography>
            </div>
          </div>
          <hr className='text-mono-color-lightest' />
          <div className='flex flex-col gap-xl md:flex-row'>
            <div className='w-[220px] flex-shrink-0'>
              <Typography weight='bold'>
                Sobre erros na plataforma durante a prova:
              </Typography>
            </div>
            <div className='flex flex-col gap-md'>
              <Typography>
                Caso aconteça algo inesperado durante a resolução da sua prova,
                tente <strong>atualizar a página.</strong> Se o erro persistir,
                tente utilizar <strong>outro navegador.</strong>
              </Typography>
              <Typography>
                Não se preocupe:{' '}
                <strong>o seu progresso será salvo automaticamente.</strong>
              </Typography>
            </div>
          </div>
        </div>
      </Container>
      <ExamInfoFooter
        onStartExamClick={onStartExam}
        isLoading={isStarting}
        label={day ? `Iniciar ${day}º dia` : 'Iniciar simuladão'}
      />
    </>
  );
};
export default ExamInfo;
